import { ToggleButton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import pfLogo from "@/assets/pf-logo.png";
import { config } from "@/config";
import { resolveTitleView } from "@/domain/proofing-flow/viewUtils";
import { useGlobalPermissions } from "@/domain/usersAndProjects/queryHooks";
import { useAuth } from "@/providers/authProvider";

import { CreateProofingTitle } from "../CreateProofingTitle/CreateProofingTitle.tsx";
import { ThemeModeToggle } from "../ThemeModeToggle.tsx";
import { UserAvatarAndMenu } from "../UserAvatarAndMenu";
import {
  ActionsContainer,
  LogoContainer,
  MainContainer,
  StyledToggleButtonGroup,
} from "./header.styles.ts";

interface HeaderProps {
  showThemeModeToggle?: boolean;
}

const Header = ({ showThemeModeToggle = false }: HeaderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, supplierGroups } = useAuth();
  // Group membership gives the supplier role and disables individual role assignments,
  // so a user with supplier groups can never also have access_overview. There is no mixed-role
  // state — supplier users get no tabs; everyone else gets the standard two-tab toggle.
  const isSupplier = supplierGroups.length > 0;

  const { permissions } = useGlobalPermissions();
  const canCreateTitle = permissions?.access_create_page ?? false;

  const titlesParam = searchParams.get("titles");
  const view = resolveTitleView(titlesParam, isSupplier);

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: string | null) => {
    if (newView !== null) {
      const newParams = new URLSearchParams(searchParams);

      if (newView === "all-titles") {
        newParams.set("titles", "all");
      } else {
        newParams.delete("titles"); // Remove param for default "your-titles"
      }

      setSearchParams(newParams);
    }
  };

  const handleSignOut = () => {
    window.location.replace(`${config.auth.serviceUrl}/authn/logout`);
  };

  return (
    <MainContainer data-testid="header-main-container">
      <LogoContainer component="a" href="/">
        <img src={pfLogo} alt="Proofing Logo" style={{ height: "32px", width: "auto" }} />
      </LogoContainer>

      {!isSupplier && (
        <StyledToggleButtonGroup
          value={view}
          exclusive
          onChange={handleViewChange}
          aria-label="title view"
        >
          <ToggleButton value="your-titles" aria-label="your titles">
            Your titles
          </ToggleButton>
          <ToggleButton value="all-titles" aria-label="all titles">
            All titles
          </ToggleButton>
        </StyledToggleButtonGroup>
      )}

      <ActionsContainer>
        {showThemeModeToggle && <ThemeModeToggle />}
        {canCreateTitle && <CreateProofingTitle />}
        {user && <UserAvatarAndMenu user={user} onSignOutClick={handleSignOut} />}
      </ActionsContainer>
    </MainContainer>
  );
};

export default Header;
